import { useState, useEffect } from 'react';
import { COLORS } from '../lib/theme';
import { useTranslation } from '../i18n';
import { Icon } from './Icon';
import { pinSrc, type PinKind } from './MapPin';
import { usePinTypeCatalog } from './PinTypeCatalogProvider';
import { GPX_AREA_TYPE } from '../lib/gpx';

// ── Types ─────────────────────────────────────────────────────────────────

type GeoPt = { lat: number; lon: number };

export type ExportBorder  = { id: string; name: string; rings: GeoPt[][] };
export type ExportArea    = { id: string; name: string; rings: GeoPt[][] };
export type ExportPin     = { id: string; label: string; type: string; lat: number; lon: number };

export type PinCategory = { type: PinKind; label: string; color: string; group: string };

export type GpxExportData = {
  mapName:        string;
  borders:        ExportBorder[];
  subAreas:       ExportArea[];
  forbiddenAreas: ExportArea[];
  pins:           ExportPin[];
};

export interface GpxExportPanelProps {
  data:          GpxExportData;
  pinCategories: PinCategory[];
  onClose:       () => void;
}

// ── GPX serialiser ────────────────────────────────────────────────────────

function buildGpx(
  data: GpxExportData,
  include: IncludeMap,
  includedPinTypes: Set<string>,
): string {
  const now = new Date().toISOString();
  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const lines: string[] = [];
  lines.push(`<?xml version="1.0" encoding="UTF-8"?>`);
  lines.push(`<gpx version="1.1" creator="WeHunt" xmlns="http://www.topografix.com/GPX/1/1">`);
  lines.push(`  <metadata>`);
  lines.push(`    <name>${esc(data.mapName)}</name>`);
  lines.push(`    <time>${now}</time>`);
  lines.push(`  </metadata>`);

  if (include.pins) {
    for (const p of data.pins) {
      if (!includedPinTypes.has(p.type)) continue;
      lines.push(`  <wpt lat="${p.lat.toFixed(6)}" lon="${p.lon.toFixed(6)}">`);
      lines.push(`    <name>${esc(p.label || p.type)}</name>`);
      lines.push(`    <type>${esc(p.type)}</type>`);
      lines.push(`  </wpt>`);
    }
  }

  const polyTrack = (name: string, type: string, polygon: GeoPt[]) => {
    if (polygon.length < 2) return;
    lines.push(`  <trk>`);
    lines.push(`    <name>${esc(name)}</name>`);
    lines.push(`    <type>${esc(type)}</type>`);
    lines.push(`    <trkseg>`);
    for (const pt of polygon)
      lines.push(`      <trkpt lat="${pt.lat.toFixed(6)}" lon="${pt.lon.toFixed(6)}"></trkpt>`);
    const first = polygon[0];
    lines.push(`      <trkpt lat="${first.lat.toFixed(6)}" lon="${first.lon.toFixed(6)}"></trkpt>`);
    lines.push(`    </trkseg>`);
    lines.push(`  </trk>`);
  };

  // One track per RING, not per area. GPX has no notion of a hole or of a
  // multi-part shape, so an imported ground of several parcels has to become
  // several tracks; concatenating them into one would draw a track running
  // between parcels across ground that is not part of either. A part number is
  // appended only when there is more than one, so a simple area keeps its name.
  const areaTracks = (name: string, type: string, rings: GeoPt[][]) =>
    rings.forEach((ring, i) =>
      polyTrack(rings.length > 1 ? `${name} (${i + 1})` : name, type, ring));

  if (include.borders)  for (const b of data.borders)        areaTracks(b.name, GPX_AREA_TYPE.border,    b.rings);
  if (include.subAreas) for (const a of data.subAreas)       areaTracks(a.name, GPX_AREA_TYPE.subArea,   a.rings);
  if (include.forbidden) for (const a of data.forbiddenAreas) areaTracks(a.name, GPX_AREA_TYPE.forbidden, a.rings);

  lines.push(`</gpx>`);
  return lines.join('\n');
}

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Types ─────────────────────────────────────────────────────────────────

type IncludeMap = {
  borders:   boolean;
  subAreas:  boolean;
  forbidden: boolean;
  pins:      boolean;
};

// ── Checkbox helpers ──────────────────────────────────────────────────────

function Checkbox({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onChange(); }}
      style={{
        width: 18,
        height: 18,
        borderRadius: 5,
        border: `2px solid ${checked ? COLORS.primary : COLORS.outline}`,
        background: checked ? COLORS.primary : 'transparent',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: COLORS.onPrimary,
        cursor: 'pointer',
        transition: 'background 0.12s, border-color 0.12s',
      }}
    >
      {checked && <Icon name="check" size={12} />}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────

export function GpxExportPanel({
  data,
  pinCategories,
  onClose,
}: GpxExportPanelProps) {
  const { t } = useTranslation('map');
  const catalog = usePinTypeCatalog();
  const [include, setInclude] = useState<IncludeMap>({
    borders:   data.borders.length > 0,
    subAreas:  data.subAreas.length > 0,
    forbidden: data.forbiddenAreas.length > 0,
    pins:      data.pins.length > 0,
  });

  const typeSummary: { cat: PinCategory; count: number }[] = (() => {
    const countMap: Record<string, number> = {};
    for (const p of data.pins) countMap[p.type] = (countMap[p.type] ?? 0) + 1;
    return pinCategories
      .filter((c) => countMap[c.type] > 0)
      .map((c) => ({ cat: c, count: countMap[c.type] }));
  })();

  const [includedPinTypes, setIncludedPinTypes] = useState<Set<string>>(
    () => new Set(typeSummary.map((r) => r.cat.type)),
  );

  const toggleLayer = (key: keyof IncludeMap) =>
    setInclude((prev) => ({ ...prev, [key]: !prev[key] }));

  const togglePinType = (type: string) =>
    setIncludedPinTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });

  const allPinTypesOn = typeSummary.every((r) => includedPinTypes.has(r.cat.type));
  const toggleAllPinTypes = () =>
    setIncludedPinTypes(
      allPinTypesOn ? new Set() : new Set(typeSummary.map((r) => r.cat.type)),
    );

  const selectedPinCount = data.pins.filter((p) => includedPinTypes.has(p.type)).length;

  const noneSelected =
    !include.borders &&
    !include.subAreas &&
    !include.forbidden &&
    !(include.pins && selectedPinCount > 0);

  const [dlState, setDlState] = useState<'idle' | 'downloading' | 'done'>('idle');

  useEffect(() => {
    if (dlState !== 'done') return;
    const t = setTimeout(onClose, 1600);
    return () => clearTimeout(t);
  }, [dlState, onClose]);

  const handleExport = () => {
    setDlState('downloading');
    setTimeout(() => {
      const gpx = buildGpx(data, include, includedPinTypes);
      const slug = data.mapName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      downloadText(`${slug || 'map'}-export.gpx`, gpx);
      setDlState('done');
    }, 0);
  };

  const layerRows: { key: keyof IncludeMap; label: string; count: number; icon: string }[] = [
    { key: 'borders',   label: t('gpxExport.borders'),   count: data.borders.length,        icon: 'shield'         },
    { key: 'subAreas',  label: t('gpxExport.subAreas'),  count: data.subAreas.length,       icon: 'subarea'        },
    { key: 'forbidden', label: t('gpxExport.forbidden'), count: data.forbiddenAreas.length, icon: 'forbidden-area' },
  ];

  return (
    <div
      data-map-panel="true"
      data-map-print="hide"
      style={{
        position: 'absolute',
        top: 16,
        right: 60,
        width: 308,
        background: COLORS.surfaceContainerLowest,
        borderRadius: 12,
        boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
        zIndex: 3,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100% - 32px)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 12px',
          height: 52,
          flexShrink: 0,
          borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.onSurface, lineHeight: 1.2 }}>
            {t('gpxExport.title')}
          </div>
          <div style={{ fontSize: 11, color: COLORS.outline, marginTop: 1 }}>{data.mapName}</div>
        </div>
        <button className="wh-btn-icon" onClick={onClose} aria-label={t('gpxExport.close')}>
          <Icon name="x" size={18} />
        </button>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>

        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: COLORS.outline, marginBottom: 2 }}>
          {t('gpxExport.selectLayers')}
        </div>

        {layerRows.map(({ key, label, count, icon }) => {
          const checked = include[key];
          const empty = count === 0;
          return (
            <button
              key={key}
              onClick={() => !empty && toggleLayer(key)}
              disabled={empty}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                width: '100%', padding: '10px 12px', borderRadius: 10, textAlign: 'left',
                border: `1.5px solid ${checked && !empty ? COLORS.primary : COLORS.surfaceContainerHighest}`,
                background: checked && !empty ? COLORS.primaryContainer : COLORS.surfaceContainerLow,
                cursor: empty ? 'not-allowed' : 'pointer',
                opacity: empty ? 0.45 : 1,
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <Checkbox checked={checked && !empty} onChange={() => !empty && toggleLayer(key)} />
              <div style={{
                width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                background: checked && !empty ? COLORS.primary : COLORS.surfaceContainerHighest,
                color: checked && !empty ? COLORS.onPrimary : COLORS.outline,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s, color 0.15s',
              }}>
                <Icon name={icon} size={16} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.onSurface }}>{label}</div>
                <div style={{ fontSize: 11, color: COLORS.outline }}>
                  {empty ? t('gpxExport.noneOnMap') : t('gpxExport.items', { count })}
                </div>
              </div>
            </button>
          );
        })}

        {data.pins.length > 0 && (
          <div style={{ borderRadius: 10, border: `1.5px solid ${include.pins ? COLORS.primary : COLORS.surfaceContainerHighest}`, overflow: 'hidden', background: include.pins ? COLORS.primaryContainer : COLORS.surfaceContainerLow, transition: 'border-color 0.15s, background 0.15s' }}>

            <button
              onClick={() => toggleLayer('pins')}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                width: '100%', padding: '10px 12px', textAlign: 'left',
                background: 'transparent', border: 'none', cursor: 'pointer',
              }}
            >
              <Checkbox checked={include.pins} onChange={() => toggleLayer('pins')} />
              <div style={{
                width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                background: include.pins ? COLORS.primary : COLORS.surfaceContainerHighest,
                color: include.pins ? COLORS.onPrimary : COLORS.outline,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s, color 0.15s',
              }}>
                <Icon name="pin" size={16} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.onSurface }}>{t('gpxExport.pins')}</div>
                <div style={{ fontSize: 11, color: COLORS.outline }}>
                  {include.pins
                    ? t('gpxExport.pinsSelected', { selected: selectedPinCount, total: data.pins.length })
                    : t('gpxExport.pinCount', { count: data.pins.length })}
                </div>
              </div>
            </button>

            {include.pins && (
              <div style={{ borderTop: `1px solid ${COLORS.surfaceContainerHighest}` }}>

                <label style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '7px 12px', cursor: 'pointer',
                  background: COLORS.surfaceContainerLowest,
                  borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
                }}>
                  <input
                    type="checkbox"
                    className="wh-checkbox"
                    checked={allPinTypesOn}
                    onChange={toggleAllPinTypes}
                  />
                  <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.outline, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {allPinTypesOn ? t('gpxExport.deselectAll') : t('gpxExport.selectAll')}
                  </span>
                </label>

                {typeSummary.map(({ cat, count }, idx) => {
                  const on = includedPinTypes.has(cat.type);
                  return (
                    <label
                      key={cat.type}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px', cursor: 'pointer',
                        background: on ? COLORS.surfaceContainerLowest : COLORS.surfaceContainer,
                        borderTop: idx > 0 ? `1px solid ${COLORS.surfaceContainerHighest}` : 'none',
                      }}
                    >
                      <input
                        type="checkbox"
                        className="wh-checkbox"
                        checked={on}
                        onChange={() => togglePinType(cat.type)}
                      />
                      <img
                        src={pinSrc(cat.type)}
                        alt={catalog.label(cat.type)}
                        width={26}
                        height={26}
                        style={{ flexShrink: 0, objectFit: 'contain' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.onSurface }}>{catalog.label(cat.type)}</div>
                        <div style={{ fontSize: 11, color: COLORS.outline }}>{t('gpxExport.pinCount', { count })}</div>
                      </div>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
                        background: COLORS.surfaceContainerHighest, color: COLORS.outline,
                      }}>
                        {count}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {data.pins.length === 0 && (
          <button disabled style={{
            display: 'flex', alignItems: 'center', gap: 12,
            width: '100%', padding: '10px 12px', borderRadius: 10, textAlign: 'left',
            border: `1.5px solid ${COLORS.surfaceContainerHighest}`,
            background: COLORS.surfaceContainerLow, cursor: 'not-allowed', opacity: 0.45,
          }}>
            <Checkbox checked={false} onChange={() => {}} />
            <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: COLORS.surfaceContainerHighest, color: COLORS.outline, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="pin" size={16} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.onSurface }}>{t('gpxExport.pins')}</div>
              <div style={{ fontSize: 11, color: COLORS.outline }}>{t('gpxExport.noneOnMap')}</div>
            </div>
          </button>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '10px 14px', flexShrink: 0,
        borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
        display: 'flex', gap: 8, justifyContent: 'flex-end',
        alignItems: 'center',
      }}>
        {dlState === 'done' ? (
          <>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, color: COLORS.primary }}>
              <div style={{
                width: 24, height: 24, borderRadius: 999,
                background: COLORS.primaryContainer,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name="check" size={14} color={COLORS.primary} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{t('gpxExport.downloaded')}</span>
            </div>
            <button className="wh-btn wh-btn-outline" onClick={onClose}>{t('gpxExport.close')}</button>
          </>
        ) : (
          <>
            <button className="wh-btn wh-btn-outline" onClick={onClose} disabled={dlState === 'downloading'}>
              {t('gpxExport.cancel')}
            </button>
            <button
              className="wh-btn wh-btn-primary"
              disabled={noneSelected || dlState === 'downloading'}
              onClick={handleExport}
              style={{ minWidth: 140 }}
            >
              {dlState === 'downloading' ? (
                <>
                  <span style={{
                    width: 14, height: 14, borderRadius: 999, flexShrink: 0,
                    border: `2px solid ${COLORS.onPrimary}`,
                    borderTopColor: 'transparent',
                    animation: 'wh-spin 0.7s linear infinite',
                    display: 'inline-block',
                  }} />
                  {t('gpxExport.preparing')}
                </>
              ) : (
                <>
                  <Icon name="download" size={16} />
                  {t('gpxExport.download')}
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
